/**
 * Shared Business Validator Helper for Frontend
 * Supports country-aware business number types, labels, placeholders, and validation for:
 * AU (ABN/ACN), NZ (NZBN/GST), US (EIN), UK/GB (CRN/VAT), SG (UEN), ES (CIF/NIF/NIE).
 */

export function normalizeCountryCode(country) {
    if (!country) return 'AU';
    const c = String(country).trim().toUpperCase();
    if (c === 'AU' || c === 'AUS' || c === 'AUSTRALIA') return 'AU';
    if (c === 'NZ' || c === 'NZL' || c === 'NEW ZEALAND') return 'NZ';
    if (c === 'US' || c === 'USA' || c === 'UNITED STATES' || c === 'UNITED STATES OF AMERICA') return 'US';
    if (c === 'UK' || c === 'GB' || c === 'GBR' || c === 'UNITED KINGDOM' || c === 'GREAT BRITAIN') return 'UK';
    if (c === 'SG' || c === 'SGP' || c === 'SINGAPORE') return 'SG';
    if (c === 'ES' || c === 'ESP' || c === 'SPAIN') return 'ES';
    return c;
}

export function isAustralianCountry(country) {
    const code = normalizeCountryCode(country);
    return code === 'AU';
}

export function getBusinessNumberType(country) {
    const code = normalizeCountryCode(country);
    switch (code) {
        case 'AU': return 'ABN';
        case 'NZ': return 'NZBN';
        case 'US': return 'EIN';
        case 'UK': return 'CRN';
        case 'SG': return 'UEN';
        case 'ES': return 'CIF';
        default: return 'ABN';
    }
}

export function getBusinessNumberLabel(country) {
    const code = normalizeCountryCode(country);
    switch (code) {
        case 'AU': return 'Australian Business Number (ABN)';
        case 'NZ': return 'New Zealand Business Number (NZBN)';
        case 'US': return 'Employer Identification Number (EIN)';
        case 'UK': return 'Company Registration Number (CRN)';
        case 'SG': return 'Unique Entity Number (UEN)';
        case 'ES': return 'Tax Identification Number (CIF/NIF)';
        default: return 'Business Number';
    }
}

export function getBusinessNumberShortLabel(country) {
    const code = normalizeCountryCode(country);
    switch (code) {
        case 'AU': return 'ABN';
        case 'NZ': return 'NZBN';
        case 'US': return 'EIN';
        case 'UK': return 'CRN';
        case 'SG': return 'UEN';
        case 'ES': return 'CIF/NIF';
        default: return 'Business Number';
    }
}

export function getBusinessNumberPlaceholder(country) {
    const code = normalizeCountryCode(country);
    switch (code) {
        case 'AU': return 'e.g. 67 611 787 029 (11 digits)';
        case 'NZ': return 'e.g. 9429000000000 or 049-098-576';
        case 'US': return 'e.g. 12-3456789 (9 digits)';
        case 'UK': return 'e.g. 01234567 or SC123456';
        case 'SG': return 'e.g. 200812345M or T08LL0001A';
        case 'ES': return 'e.g. B82387770 or 12345678Z';
        default: return 'Enter Business Number';
    }
}

export function validateBusinessNumber(value, country) {
    if (!value || typeof value !== 'string' || !value.trim()) {
        return true; // Optional unless required validator is separately applied
    }
    const clean = value.trim();
    const code = normalizeCountryCode(country);

    switch (code) {
        case 'AU': {
            const digits = clean.replace(/\s+/g, '');
            return digits.length === 11 || digits.length === 9;
        }
        case 'NZ': {
            const digits = clean.replace(/[\s-]+/g, '');
            return digits.length === 13 || digits.length === 8 || digits.length === 9;
        }
        case 'US': {
            const digits = clean.replace(/[\s-]+/g, '');
            return digits.length === 9;
        }
        case 'UK': {
            const trimmed = clean.replace(/[\s-]+/g, '');
            return trimmed.length >= 6 && trimmed.length <= 11;
        }
        case 'SG': {
            const trimmed = clean.replace(/[\s-]+/g, '');
            return trimmed.length === 9 || trimmed.length === 10;
        }
        case 'ES': {
            const trimmed = clean.replace(/[\s-]+/g, '');
            return trimmed.length === 9;
        }
        default:
            return clean.length >= 4 && clean.length <= 30;
    }
}

export default {
    normalizeCountryCode,
    isAustralianCountry,
    getBusinessNumberType,
    getBusinessNumberLabel,
    getBusinessNumberShortLabel,
    getBusinessNumberPlaceholder,
    validateBusinessNumber,
};
